import { useCallback, useEffect, useState } from 'react'

export interface ClarifyLayoutActiveApi {
  layoutActive: boolean
  handleExitComplete: () => void
}

/**
 * Keeps the `.quick-notes-page--clarify-mode` layout class applied for the
 * full duration of the AnimatePresence exit transition.
 *
 * If we bound the class directly to `clarifyMode`, the class would be removed
 * the instant the user exits — but AnimatePresence still renders the clarify
 * block for another ~180 ms while fading it out. During that window the page
 * would lose `height: 100%; overflow: hidden; padding-bottom: 0`, which
 * re-flows the inner ClarifyEntryList scroll container and visibly "fans out"
 * the stacked cards through the fade.
 *
 * Lifecycle:
 *   - clarifyMode true  → layoutActive true (immediate)
 *   - clarifyMode false → layoutActive stays true until onExitComplete fires
 *   - onExitComplete fires while clarifyMode is still true → ignored
 */
export function useClarifyLayoutActive(clarifyMode: boolean): ClarifyLayoutActiveApi {
  const [layoutActive, setLayoutActive] = useState(clarifyMode)

  useEffect(() => {
    if (clarifyMode) setLayoutActive(true)
  }, [clarifyMode])

  const handleExitComplete = useCallback(() => {
    setLayoutActive(prev => (clarifyMode ? prev : false))
  }, [clarifyMode])

  return { layoutActive, handleExitComplete }
}
